import { useEffect, useRef, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import { Box, Button, ClickAwayListener, Grid, Popper, Typography, useTheme } from '@mui/material'
import i18n, { getFullNameByLocale, getMultipleLanguageByCountry } from 'utils/i18n'

import { languageDropdownSx } from './styled'

interface LanguageDropdownProps {
  cnDomainRegx: any
  currentPath: string
  isRtl: boolean
  iconArrow: any
}

const LanguageDropdown = (props: LanguageDropdownProps) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const { countryCode, language } = i18n
  const [listCountry, setListCountry] = useState<
    { language: string; locale: string; country: string; fullName: string; isRtl: boolean }[]
  >([])
  const [isDisabled, setIsDisabled] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const languageAndLocation = getFullNameByLocale(language)
  const prevOpen = useRef(isOpen)

  useEffect(() => {
    disableChangeLanguage()
  }, [listCountry])

  useEffect(() => {
    if (countryCode) {
      const languages = getMultipleLanguageByCountry(countryCode)
      setListCountry(languages)
    }
  }, [countryCode])

  useEffect(() => {
    if (prevOpen.current === true && isOpen === false) {
      if (anchorRef.current) {
        anchorRef.current.focus()
      }
    }
    prevOpen.current = isOpen
  }, [isOpen])

  const openMenu = () => {
    if (isOpen) {
      closeMenu()
    } else {
      setIsOpen(true)
    }
  }

  const closeMenu = () => {
    setIsOpen(false)
  }

  const disableChangeLanguage = () => {
    if (listCountry.length < 2) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }

  const handleLanguageSelect = (locale: string) => {
    closeMenu()
    if (language !== locale) {
      i18n.changeLanguage(locale)
    }
  }

  const escClosePopper = (event: React.KeyboardEvent<HTMLElement>) => {
    if (isOpen && event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
    }
  }

  const buildLangRtl = (rtlLanguage: string, rtlCountry: string) => {
    return rtlLanguage + ' / ' + rtlCountry
  }

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <Box role='presentation' data-testid='BoxFFA2DA4D'>
        {isDisabled ? (
          <Typography sx={languageDropdownSx.textLang(true)}>{languageAndLocation}</Typography>
        ) : (
          <Button
            ref={anchorRef}
            sx={languageDropdownSx.buttonLang(!props.cnDomainRegx.test(props.currentPath))}
            onClick={openMenu}
            onKeyDown={escClosePopper}
            disableRipple
            data-testid='Button62394AC2'
          >
            <Typography sx={languageDropdownSx.textLang(false)}>{languageAndLocation}</Typography>
            {!props.cnDomainRegx.test(props.currentPath) && (
              <Box
                component='img'
                alt=''
                src={props.iconArrow}
                sx={languageDropdownSx.imageIcon(props.isRtl, theme.palette.mode === 'dark', isOpen)}
              />
            )}
          </Button>
        )}

        <Popper open={isOpen} anchorEl={anchorRef.current} placement='top-start' disablePortal>
          <Box sx={languageDropdownSx.popper(anchorRef.current?.offsetWidth)}>
            {listCountry.map((data) => (
              <Grid
                key={data.locale}
                container
                sx={{
                  width: '100%'
                }}
              >
                <Button
                  onClick={() => handleLanguageSelect(data.locale)}
                  onKeyDown={escClosePopper}
                  sx={languageDropdownSx.listItemButton}
                  disableRipple
                  data-testid='Button32ABF6CE'
                >
                  <Grid item xs={10} sx={{ textAlign: props.isRtl ? 'right' : 'left' }}>
                    <Typography sx={languageDropdownSx.text(data.locale === language)}>
                      {data.isRtl && props.isRtl ? buildLangRtl(data.language, data.country) : data.fullName}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', alignSelf: 'flex-start' }}
                  >
                    {data.locale === language && <CheckIcon sx={languageDropdownSx.muiIcon} />}
                  </Grid>
                </Button>
              </Grid>
            ))}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default LanguageDropdown
